import { Injectable, OnDestroy, inject } from '@angular/core';
import {
    firstValueFrom,
    Subject,
    catchError,
    of,
} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from 'src/config';
import { NotificationService } from 'src/app/modules/shared/services/notification.service';
import { TranslateService } from './translate.service';
import { Garage } from '../../shared/models/garage';
import * as _ from 'lodash';
import { LoggingService } from '@modules/shared/services/logging.service';
import {
    InformationTemplate,
    TrafficDeviationsStore,
} from '@components/traffic-deviations/stores/traffic-deviations.store';
import { TaService } from './ta.service';
import { handleError } from './service.helpers';
import { TrafficDeviationAccessService } from './traffic-deviation-access.service';

@Injectable({
    providedIn: 'root',
})
export class DataSyncService implements OnDestroy {
    private readonly _translateService = inject(TranslateService);
    private readonly _notificationService = inject(NotificationService);
    private readonly _taService = inject(TaService);
    private readonly _trafficDeviationsStore = inject(TrafficDeviationsStore);
    private readonly _trafficDeviationAccessService = inject(TrafficDeviationAccessService);
    private readonly _config = inject(ConfigService);
    private readonly _http = inject(HttpClient);
    private readonly _logger = inject(LoggingService);

    private readonly _ngUnsubscribe$ = new Subject<void>();



    constructor() {}

    ngOnDestroy(): void {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
    }

    public init() {
        this._trafficDeviationAccessService.hasAccess().then((hasAccess) => {
            if (hasAccess?.TaWriter || hasAccess?.TemplateEditor)
                this._loadTrafficDeviationInformationTemplates();
        });
    }

    private async _retryHttp<T>(
        url: string,
        body: any = null,
        type: 'put' | 'post' = 'put',
        maxRetries = 5,
    ): Promise<T> {
        let attempt = 0;
        while (attempt < maxRetries) {
            try {
                if (type === 'put')
                    return await firstValueFrom(this._http.put<T>(url, body));
                else return await firstValueFrom(this._http.post<T>(url, body));
            } catch (error) {
                // Check if it's a ProgressEvent (network error, e.g. after SignalR reconnect)
                if (error?.error instanceof ProgressEvent) {
                    attempt++;
                    if (attempt >= maxRetries) {
                        throw error;
                    }
                    await new Promise((res) => setTimeout(res, Math.min(1000 * Math.pow(2, attempt - 1), 10000)));
                } else {
                    // Other errors are logged directly
                    this._logger.logException(error);
                    throw error;
                }
            }
        }
    }

    private _retryHttpPut<T>(url: string) {
        return this._retryHttp<T>(url);
    }

    private _retryHttpPost<T>(url: string, body: any) {
        return this._retryHttp<T>(url, body, 'post');
    }

    private _publishCm(garageId: number, connectionId: string, isReconnect = false) {
        const handleResponse = (response: { success: boolean }) => {
            if (response?.success === false) {
                this._notificationService.openText(
                    this._translateService.instant(
                        'PublishCurrentState unsuccessful!',
                    ),
                );
                console.error(
                    'PublishCurrentState from CM unsuccessful:',
                    response,
                );
            }
        };

        const handleError = (error) => {
            if (isReconnect && error?.error instanceof ProgressEvent) {
                this._logger.logTrace(`publishCm network error after retries (garageId: ${garageId}): ${error?.message}`);
            } else {
                this._logger.logException(error);
            }
        };

        const baseUrl = this._config.cmApiBaseUrl;
        return Promise.all([
            this._retryHttpPut<{ success: boolean }>(
                `${baseUrl}/api/garages/${garageId}/publishLowSoCWarnings?connectionId=${connectionId}`,
            )
                .then(handleResponse)
                .catch(handleError),
            this._retryHttpPut<{ success: boolean }>(
                `${baseUrl}/api/garages/${garageId}/publishParkingWarnings?connectionId=${connectionId}`,
            )
                .then(handleResponse)
                .catch(handleError),
            this._retryHttpPut<{ success: boolean }>(
                `${baseUrl}/api/vehicles/${garageId}/publishStateOfChargeMessages?connectionId=${connectionId}`,
            )
                .then(handleResponse)
                .catch(handleError),
            this._retryHttpPut<{ success: boolean }>(
                `${baseUrl}/api/garages/${garageId}/publishLowSocPrognosisWarningMessages?connectionId=${connectionId}`,
            )
                .then(handleResponse)
                .catch(handleError),
        ]);
    }

    private _publishTa(garageCode: string, connectionId: string, isReconnect = false) {
        return this._retryHttpPut<{ success: boolean }>(
            `${this._config.taApiBaseUrl}/v1/trafficdeviations/${garageCode}/publish?connectionId=${connectionId}`,
        )
            .then((response) => {
                if (response?.success === false) {
                    this._notificationService.openText(
                        this._translateService.instant(
                            'PublishCurrentState unsuccessful!',
                        ),
                    );
                    console.error(
                        'PublishCurrentState from TA unsuccessful:',
                        response,
                    );
                }
            })
            .catch((error) => {
                if (isReconnect && error?.error instanceof ProgressEvent) {
                    this._logger.logTrace(`publishTa network error after retries (garageCode: ${garageCode}): ${error?.message}`);
                } else {
                    this._logger.logException(error);
                }
            });
    }

    public publishCurrentState(garages: Garage[], connectionId: string, isReconnect = false) {
        const garageCodes = garages.map(({ code }) => code);
        const garageIds = garages.map(({ id }) => id);
        const trafficDeviationGarageCodes = garages
            .filter((garage) => {
                return this._trafficDeviationAccessService.isTrafficDeviationRegion(
                    garage.hastusRegionCode,
                );
            })
            .map(({ code }) => code);

        _.each(garageIds, (garageId) => this._publishCm(garageId, connectionId, isReconnect));
        _.each(trafficDeviationGarageCodes, (garageCode) =>
            this._publishTa(garageCode, connectionId, isReconnect),
        );

        return this._retryHttpPost<{ success: boolean }>(
            this._config.mapApiBaseUrl + '/v1/realtime/publishcurrentstate',
            JSON.stringify({ garageCodes, connectionId }),
        )
            .then((response) => {
                if (response?.success === false) {
                    this._notificationService.openText(
                        this._translateService.instant(
                            'PublishCurrentState unsuccessful!',
                        ),
                    );
                    console.error(
                        'PublishCurrentState unsuccessful :',
                        response,
                    );
                }
            })
            .catch((error) => {
                if (isReconnect && error?.error instanceof ProgressEvent) {
                    this._logger.logTrace(`publishCurrentState network error after retries: ${error?.message}`);
                } else {
                    this._logger.logException(error);
                }
            });
    }

    public publishLowSocPrognosisWarnings(garages: Garage[], connectionId: string, isReconnect = false) {
        const handleResponse = (response: { success: boolean }) => {
            if (response?.success === false) {
                this._notificationService.openText(
                    this._translateService.instant('PublishCurrentState unsuccessful!'),
                );
                console.error(
                    'PublishLowSocPrognosisWarnings from CM unsuccessful:',
                    response,
                );
            }
        };

        const handleError = (error) => {
            if (isReconnect && error?.error instanceof ProgressEvent) {
                this._logger.logTrace(`publishLowSocPrognosisWarnings network error after retries: ${error?.message}`);
            } else {
                this._logger.logException(error);
            }
        };

        const baseUrl = this._config.cmApiBaseUrl;
        return Promise.all(
            garages.map((garage) =>
                this._retryHttpPut<{ success: boolean }>(
                    `${baseUrl}/api/garages/${garage.id}/publishLowSocPrognosisWarningMessages?connectionId=${connectionId}`,
                )
                    .then(handleResponse)
                    .catch(handleError),
            ),
        );
    }

    private _loadTrafficDeviationInformationTemplates(): void {
        this._taService
            .getTrafficDeviationInformationTemplates()
            .pipe(
                catchError((error) => {
                    handleError(error);
                    return of(undefined);
                }),
            )
            .subscribe((informationTemplates: InformationTemplate[]) => {
                if (informationTemplates) {
                    this._trafficDeviationsStore.updateTrafficDeviationState({
                        informationTemplates,
                    });
                }
            });
    }
}
